import type { ReactNode } from "react";
import { GripVertical, Plus, Trash2, UserPlus, X } from "lucide-react";
import { cn } from "@/lib/utils";
import type { BoardPlayer } from "./teamsBoardModel";

/**
 * Presentational pieces of the Teams board, kept apart from the board's state
 * and drag wiring so each can be read — and tested — on its own.
 *
 * Icon buttons here carry explicit `border-0 bg-transparent p-0`: a bare
 * <button> otherwise inherits the browser's default chrome, which shows as a
 * grey outline the design does not have.
 *
 * Type sits on the DESIGN.md ramp — label (text-xs/Poppins) and body
 * (text-sm/Mulish) — rather than the artboard's half-pixel sizes. The warning
 * hue is the `--warn` token, which carries its own dark-mode value, so nothing
 * here needs a `dark:` colour twin.
 */

/** Initials bubble. `muted` is the rail's lighter treatment. */
export function Avatar({
  initials,
  muted = false,
}: {
  initials: string;
  muted?: boolean;
}) {
  return (
    <span
      aria-hidden
      className={cn(
        "flex shrink-0 items-center justify-center rounded-full text-xs font-semibold",
        muted
          ? "size-7 border border-border bg-surface text-text-muted"
          : "size-8 bg-primary/10 text-primary",
      )}
    >
      {initials}
    </span>
  );
}

/** "12.4 hcp", or nothing at all when the player has no handicap yet. */
export function HandicapMeta({ handicap }: { handicap?: number }) {
  // `== null`, not truthiness — a scratch golfer's 0 is a real handicap.
  if (handicap == null) return null;
  return (
    <span className="mulish mt-0.5 block text-xs tabular-nums text-text-muted">
      {handicap.toFixed(1)} HCP
    </span>
  );
}

export function StatPill({
  children,
  tone = "default",
}: {
  children: ReactNode;
  tone?: "default" | "ok" | "warn";
}) {
  return (
    <span
      className={cn(
        "mulish inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium",
        tone === "warn"
          ? "border-warn/35 bg-warn/10 text-warn"
          : "border-border bg-surface text-text-muted",
      )}
    >
      {children}
    </span>
  );
}

/**
 * Incomplete-teams warning — a Finalize blocker, not advice.
 *
 * The backend refuses to finalize a team league while any player is without a
 * full team, so this speaks in those terms. It covers two situations with
 * different remedies, and says which one the operator is in:
 *
 *  - the roster doesn't divide by the team size (7 players in pairs), so no
 *    amount of assigning will help — the Roster itself has to change;
 *  - the roster divides fine and the players simply aren't all placed yet.
 *
 * Written against the league's own team size rather than assuming pairs: at
 * teams of 4, a roster of 8 divides evenly and 10 leaves two players over, so
 * "one player without a teammate" is wrong twice over there.
 */
export function IncompleteTeamsBanner({
  needsPartner,
  unassignedCount,
  rosterCount,
  teamSize = 2,
}: {
  /**
   * Players who are not on a complete team: everyone unassigned, plus anyone
   * sitting on a team that still has an open slot. Used when the rail is
   * already empty and a team is still short.
   */
  needsPartner: number;
  /** Players still sitting in the unassigned rail. */
  unassignedCount: number;
  rosterCount: number;
  teamSize?: number;
}) {
  const leftOver = teamSize > 0 ? rosterCount % teamSize : 0;
  const waiting = unassignedCount > 0 ? unassignedCount : needsPartner;
  const playerWord = waiting === 1 ? "player" : "players";
  const leftoverWord =
    leftOver === 1
      ? "One player still needs a teammate."
      : `${leftOver} players still need teammates.`;
  const message =
    leftOver > 0
      ? `Every team requires ${teamSize} players. ${leftoverWord}`
      : `${waiting} ${playerWord} still ${waiting === 1 ? "needs" : "need"} to be assigned to a complete team.`;
  return (
    <div
      role="status"
      className="flex items-start gap-3 rounded-xl border border-warn/35 bg-warn/10 p-4"
    >
      <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-surface text-warn">
        <UserPlus className="size-4" aria-hidden />
      </span>
      <p className="mulish text-sm leading-relaxed text-text-primary">
        {message}
      </p>
    </div>
  );
}

/** A filled slot on a team card. */
export function FilledSlot({
  player,
  onRemove,
  disabled,
}: {
  player: BoardPlayer;
  /** Omitted on a read-only board — the slot stays, the remove control does not. */
  onRemove?: () => void;
  disabled?: boolean;
}) {
  return (
    // animate-slot-in runs once, on mount — this node exists only because a
    // player just landed here, so the arrival is the thing being acknowledged.
    <div className="animate-slot-in flex items-center gap-3 rounded-xl bg-muted/60 px-3 py-2">
      <Avatar initials={player.initials} />
      <span className="min-w-0">
        <span className="mulish block text-sm font-semibold break-words text-text-primary">
          {player.name}
        </span>
        <HandicapMeta handicap={player.handicap} />
      </span>
      {onRemove && (
        <button
          type="button"
          onClick={onRemove}
          disabled={disabled}
          aria-label={`Remove ${player.name} from this team`}
          className="ml-auto flex size-6 shrink-0 items-center justify-center rounded-md border-0 bg-transparent p-0 text-text-muted transition-colors hover:text-text-primary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary disabled:opacity-50"
        >
          <X className="size-3.5" aria-hidden />
        </button>
      )}
    </div>
  );
}

/** An empty slot: a drop target, and a click target for click-to-place. */
export function EmptySlot({
  label = "Drop player here",
  isOver = false,
  isArmed = false,
  onClick,
  innerRef,
}: {
  label?: string;
  /** A dragged player is hovering this slot. */
  isOver?: boolean;
  /** A player is selected in the rail, so clicking places them here. */
  isArmed?: boolean;
  onClick?: () => void;
  innerRef?: (el: HTMLElement | null) => void;
}) {
  // Always a button, even when it can't be clicked: switching element type
  // between armed and idle remounts the node, and this node is a dnd-kit drop
  // target — dnd-kit measures it at drag start, so a remount mid-drag leaves it
  // holding a stale reference and the drop lands nowhere.
  return (
    <button
      ref={innerRef as (el: HTMLButtonElement | null) => void}
      type="button"
      onClick={onClick}
      disabled={!onClick}
      aria-hidden={!onClick}
      tabIndex={onClick ? 0 : -1}
      className={cn(
        "poppins flex w-full items-center justify-center gap-2 rounded-xl border-[1.5px] border-dashed bg-transparent px-3 py-3.5 text-xs font-medium transition-[color,background-color,border-color,transform] duration-150 motion-reduce:transition-none",
        onClick &&
          "cursor-pointer focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary",
        isOver
          ? "scale-[1.02] border-primary bg-primary/10 text-primary"
          : isArmed
            ? "border-primary bg-primary/10 text-primary"
            : "border-border text-text-muted",
        onClick &&
          !isOver &&
          !isArmed &&
          "hover:border-primary/40 hover:text-text-primary",
      )}
    >
      <Plus className="size-3.5" aria-hidden />
      {label}
    </button>
  );
}

/** A draggable player in the Unassigned rail. */
export function PlayerChip({
  player,
  isSelected = false,
  isDragging = false,
  disabled = false,
  onClick,
  innerRef,
  dragProps,
}: {
  player: BoardPlayer;
  isSelected?: boolean;
  isDragging?: boolean;
  /** A placement is in flight; this chip can't start another. */
  disabled?: boolean;
  onClick?: () => void;
  innerRef?: (el: HTMLElement | null) => void;
  dragProps?: Record<string, unknown>;
}) {
  return (
    <button
      ref={innerRef as (el: HTMLButtonElement | null) => void}
      type="button"
      onClick={onClick}
      disabled={disabled}
      // Spread before aria-pressed: dnd-kit's draggable attributes carry their
      // own aria-pressed, which is undefined while idle and would otherwise
      // strip the selected state this chip is reporting.
      {...dragProps}
      aria-pressed={isSelected}
      className={cn(
        // touch-none: without it the browser claims the gesture for scrolling
        // and the drag never starts on a touch screen.
        "flex w-full touch-none cursor-grab items-center gap-2 rounded-xl border px-3 py-2 text-left transition-colors select-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary disabled:cursor-default disabled:opacity-50",
        isSelected
          ? "border-primary bg-primary/10"
          : "border-transparent bg-muted/60 hover:border-primary/30",
        isDragging && "opacity-40",
      )}
    >
      <GripVertical className="size-3 shrink-0 text-text-muted" aria-hidden />
      <Avatar initials={player.initials} muted />
      <span className="min-w-0">
        <span className="mulish block break-words text-sm font-semibold text-text-primary">
          {player.name}
        </span>
        <HandicapMeta handicap={player.handicap} />
      </span>
    </button>
  );
}

/** Dashed "add a team" tile that sits in the grid alongside the team cards. */
export function AddTeamCard({
  onClick,
  disabled = false,
}: {
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="poppins flex min-h-32 flex-col items-center justify-center gap-2 rounded-md border-[1.5px] border-dashed border-border bg-transparent text-sm font-medium text-text-muted transition-colors hover:border-primary/40 hover:text-text-primary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary disabled:pointer-events-none disabled:opacity-50"
    >
      <Plus className="size-5 text-text-muted" aria-hidden />
      Add Team
    </button>
  );
}

/** Section label for the large-league split: needs attention vs complete. */
export function SectionHeading({
  label,
  count,
}: {
  label: string;
  count: number;
}) {
  return (
    <div className="mb-3 flex items-center gap-2">
      <h3 className="poppins text-xs font-semibold uppercase tracking-[0.08em] text-text-muted">
        {label}
      </h3>
      <span className="mulish shrink-0 rounded-full bg-muted/60 px-2 py-0.5 text-xs font-semibold tabular-nums text-text-muted">
        {count}
      </span>
    </div>
  );
}

/** Delete control on a team card header. */
export function DeleteTeamButton({
  teamName,
  onClick,
}: {
  teamName: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={`Delete ${teamName}`}
      className="flex size-6 shrink-0 items-center justify-center rounded-md border-0 bg-transparent p-0 text-text-muted transition-colors hover:text-destructive focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
    >
      <Trash2 className="size-3.5" aria-hidden />
    </button>
  );
}

/**
 * "Needs partner" tag, shown on a card with an open slot. Inline by default
 * (TeamRow); the card positions it onto its top border via className.
 */
export function NeedsPartnerTag({ className }: { className?: string }) {
  return (
    <span
      className={cn(
        "poppins shrink-0 whitespace-nowrap rounded-full border border-warn/35 bg-surface px-2 py-0.5 text-xs font-medium uppercase tracking-[0.08em] text-warn",
        className,
      )}
    >
      Needs partner
    </span>
  );
}
